/**
 * "Painel". build-prompt section 6.
 *
 * Counts, the pending list, and the two things she actually does from here:
 * import a list and send invitations.
 */

import { useEffect, useState } from 'react';
import {
  api,
  ApiError,
  host,
  type DashboardView,
  type PublicEvent,
} from '../api.js';
import { navigate } from '../router.js';
import { Art } from './Art.js';

export function Dashboard({ eventId }: { eventId: string }) {
  const [view, setView] = useState<DashboardView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    host
      .dashboard(eventId)
      .then(setView)
      .catch((err: Error) => setError(err.message));
  }, [eventId]);

  if (error) {
    return (
      <div className="page">
        <div className="banner error">{error}</div>
      </div>
    );
  }

  if (!view) return <div className="spinner">Carregando…</div>;

  const { event, counts, pending } = view;
  const bands = Object.entries(counts.stayingChildrenByBand).filter(
    ([, n]) => n > 0,
  );

  return (
    <div className="page">
      <h1>
        {event.childName}, {event.ageTurning} anos
      </h1>
      <p className="muted">
        {event.whenText} · {event.venue}
      </p>

      <EventDetails
        eventId={eventId}
        event={event}
        onSaved={(next) =>
          setView((current) =>
            current === null ? current : { ...current, event: next },
          )
        }
      />

      <h2>Confirmados</h2>
      <div className="counts">
        <Count n={counts.stayingAdults} label="adultos ficando" />
        <Count n={counts.stayingChildren} label="crianças ficando" />
        <Count n={counts.dropoff} label="deixam e voltam" />
        <Count n={counts.notAttending} label="não vêm" />
        <Count n={counts.pending} label="sem resposta" />
      </div>

      {bands.length > 0 && (
        <>
          <h2>Crianças por idade</h2>
          <div className="card">
            {bands.map(([band, n]) => (
              <div className="row" key={band}>
                <div className="grow">{band === 'unknown' ? 'idade não informada' : `${band} anos`}</div>
                <strong>{n}</strong>
              </div>
            ))}
          </div>
        </>
      )}

      <h2>Recado para as famílias</h2>
      <EventMessage
        eventId={eventId}
        initial={event.message ?? ''}
        onSaved={(message) =>
          setView((current) =>
            current === null
              ? current
              : { ...current, event: { ...current.event, message } },
          )
        }
      />

      <h2>Arte</h2>
      <Art
        eventId={eventId}
        event={event}
        canGenerate={view.canGenerateArt}
        onChanged={(artUrl) =>
          setView((current) =>
            current === null
              ? current
              : { ...current, event: { ...current.event, artUrl } },
          )
        }
      />

      <h2>O que fazer agora</h2>
      <a className="button block" href={`/convites/${eventId}`}>
        Convites e famílias
      </a>
      <a
        className="button block"
        href={`/importar/${eventId}`}
        style={{ marginTop: '0.5rem' }}
      >
        Importar lista da turma
      </a>
      <a
        className="button block"
        href={`/recepcao/${eventId}`}
        style={{ marginTop: '0.5rem' }}
      >
        Recepção: quem já chegou
      </a>

      <h2>Baixar a lista</h2>
      <p className="muted">
        A qualquer momento, sem esperar nada. O e-mail depois da festa é só uma
        segunda via.
      </p>
      <a className="button block" href={`/api/events/${eventId}/lista.csv`}>
        Lista de convidados
      </a>
      <a
        className="button block"
        href={`/api/events/${eventId}/lista.csv?tipo=presenca`}
        style={{ marginTop: '0.5rem' }}
      >
        Lista de presença
      </a>

      <h2>Faltam responder ({pending.length})</h2>
      <p className="muted">Toque em uma família para ver quem está no convite.</p>
      <div className="card">
        {pending.length === 0 ? (
          <p className="muted">Todo mundo já respondeu.</p>
        ) : (
          pending.map((household) => (
            /*
             * Tappable, because this is where she looks first when something
             * is wrong with a family, and a row that lists a name without
             * letting her open it is a dead end.
             */
            <button
              className="row row-button"
              key={household.householdId}
              onClick={() =>
                navigate(`/convites/${eventId}?abrir=${household.householdId}`)
              }
            >
              <div className="grow">
                <div className="label-main">{household.label}</div>
              </div>
              <span
                className={`tag ${household.openedAt ? 'ok' : household.sentAt ? 'warn' : ''}`}
              >
                {household.openedAt
                  ? 'abriu'
                  : household.sentAt
                    ? 'enviado'
                    : 'não enviado'}
              </span>
              <span aria-hidden="true" className="chevron">›</span>
            </button>
          ))
        )}
      </div>

      <DeleteEverything eventId={eventId} childName={event.childName} />
    </div>
  );
}

/**
 * "Apagar tudo agora". build-prompt section 10.
 *
 * There is no undo and no backup, so it asks her to type the child's name
 * rather than relying on a dialog nobody reads.
 */
function DeleteEverything({
  eventId,
  childName,
}: {
  eventId: string;
  childName: string;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <>
        <h2>Apagar tudo</h2>
        <p className="muted">
          Some com tudo agora: nomes, idades e respostas. Sem volta.
        </p>
        <button className="danger-outline" onClick={() => setOpen(true)}>
          Apagar tudo agora
        </button>
      </>
    );
  }

  return (
    <>
      <h2>Apagar tudo</h2>
      <div className="card">
        <div className="banner error">
          Isso apaga tudo desta festa e não dá para desfazer. Baixe a lista
          antes, se quiser guardar.
        </div>

        {error && <div className="banner error">{error}</div>}

        <label htmlFor="confirm">
          Escreva “{childName}” para confirmar
        </label>
        <input
          id="confirm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
        />

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button
            className="danger"
            disabled={busy || typed.trim().toLowerCase() !== childName.trim().toLowerCase()}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.post(`/api/events/${eventId}/apagar`, { confirm: typed });
                navigate('/');
              } catch (err) {
                setError(
                  err instanceof ApiError ? err.message : 'Não deu certo.',
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Apagando…' : 'Apagar tudo'}
          </button>
          <button onClick={() => setOpen(false)}>Cancelar</button>
        </div>
      </div>
    </>
  );
}

/**
 * The party itself, editable.
 *
 * It used to be set once at creation and never again, so a wrong age was a
 * wrong age on every invitation for ever. Closed by default, because most days
 * there is nothing to change here.
 */
function EventDetails({
  eventId,
  event,
  onSaved,
}: {
  eventId: string;
  event: PublicEvent;
  onSaved: (event: PublicEvent) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [values, setValues] = useState({
    childName: event.childName,
    ageTurning: String(event.ageTurning),
    venue: event.venue,
    date: event.startsAt.slice(0, 10),
    time: event.startsAt.slice(11, 16),
    rsvpDeadline: event.rsvpDeadline,
  });

  const set = (key: keyof typeof values) => (value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setSaved(false);
  };

  if (!open) {
    return (
      <button className="linkish" onClick={() => setOpen(true)}>
        Corrigir os dados da festa
      </button>
    );
  }

  return (
    <div className="card">
      {error && <div className="banner error">{error}</div>}
      {saved && <div className="banner ok">Salvo. O convite já mostra isso.</div>}

      <div className="row-2 name-age">
        <div>
          <label htmlFor="child">Aniversariante</label>
          <input
            id="child"
            value={values.childName}
            onChange={(e) => set('childName')(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="age">Faz quantos anos</label>
          <input
            id="age"
            type="number"
            inputMode="numeric"
            min={0}
            max={120}
            value={values.ageTurning}
            onChange={(e) => set('ageTurning')(e.target.value)}
          />
        </div>
      </div>

      <div className="row-2 when">
        <div>
          <label htmlFor="date">Dia</label>
          <input
            id="date"
            type="date"
            value={values.date}
            onChange={(e) => set('date')(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="time">Hora</label>
          <input
            id="time"
            type="time"
            value={values.time}
            onChange={(e) => set('time')(e.target.value)}
          />
        </div>
      </div>

      <label htmlFor="venue">Onde</label>
      <input
        id="venue"
        value={values.venue}
        onChange={(e) => set('venue')(e.target.value)}
      />

      <label htmlFor="deadline">Confirmar até</label>
      <input
        id="deadline"
        type="date"
        value={values.rsvpDeadline}
        onChange={(e) => set('rsvpDeadline')(e.target.value)}
      />

      <p className="muted" style={{ marginTop: '0.75rem' }}>
        Vale para todos os convites na hora. Quem já recebeu a mensagem no
        WhatsApp continua com o texto antigo, mas o link mostra o certo.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button
          className="primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const { event: next } = await host.editEvent(eventId, {
                childName: values.childName,
                ageTurning: Number(values.ageTurning),
                venue: values.venue,
                startsAt: `${values.date}T${values.time || '00:00'}:00${offsetOf(event.startsAt)}`,
                rsvpDeadline: values.rsvpDeadline,
              });
              onSaved(next);
              setSaved(true);
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Não deu certo.');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Salvando…' : 'Salvar'}
        </button>
        <button onClick={() => setOpen(false)}>Fechar</button>
      </div>
    </div>
  );
}

/** Keep the party's own offset rather than the browser's. */
function offsetOf(startsAt: string): string {
  return startsAt.slice(19) || '-03:00';
}

/**
 * One message, shown on every invitation.
 *
 * The place for "vai ter piscina, tragam roupa de banho", which she would
 * otherwise repeat in twenty-one WhatsApp messages and still forget one.
 */
function EventMessage({
  eventId,
  initial,
  onSaved,
}: {
  eventId: string;
  initial: string;
  onSaved: (message: string | null) => void;
}) {
  const [text, setText] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  return (
    <div className="card">
      <textarea
        rows={3}
        maxLength={600}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setSaved(false);
        }}
        placeholder="Ex.: vai ter piscina, tragam roupa de banho. Os pais podem ficar."
      />
      <div className="hint-inline">
        <span>{saved ? 'Salvo.' : 'Aparece no convite de todas as famílias.'}</span>
        {text !== initial && (
          <button
            className="linkish"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const { message } = await host.editEvent(eventId, { message: text });
                onSaved(message);
                setSaved(true);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'salvando…' : 'salvar recado'}
          </button>
        )}
      </div>
    </div>
  );
}

function Count({ n, label }: { n: number; label: string }) {
  return (
    <div className="count">
      <div className="n">{n}</div>
      <div className="label">{label}</div>
    </div>
  );
}
